import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Platform } from '@ionic/angular';
import { v4 } from 'uuid';

import { EnvironmentService } from '../environment/environment.service';
import { TenantConfigService } from '../tenant-config/tenant-config.service';

@Injectable({ providedIn: 'root' })
export class NativeShareService {
  private browserShareTarget: Promise<string> = this.tenantConfigService.getBrowserShareTarget();

  constructor(
    private _location: Location,
    private platform: Platform,
    private tenantConfigService: TenantConfigService,
    private environmentService: EnvironmentService
  ) {}

  public async share(options: ShareOptions): Promise<boolean> {
    const canShare = await Share.canShare();
    let shareSucceeded = true;
    const filesOutput: string[] = [];
    if (canShare.value) {
      if (options.files) {
        for (const file of options.files) {
          // regex to check for invalid chars global
          const checkForInvalidCharsRegex = /[<>:"\/\\|?*]/g;
          let filename = file.filename ?? v4();
          if (filename.search(checkForInvalidCharsRegex) !== -1) {
            filename = filename.replace(checkForInvalidCharsRegex, '_');
          }
          // file.uri is probably never used by us
          let filePath = file.uri;
          if (!filePath && file.base64) {
            try {
              const fileResult = await Filesystem.writeFile({
                directory: Directory.Cache,
                path: filename,
                data: file.base64,
              });
              filePath = fileResult.uri;
            } catch (error) {
              console.error(error);
              throw Error('Share failed. Cannot create temp file.');
            }
          }
          filesOutput.push(filePath);
        }
        if (filesOutput && filesOutput.length > 0) {
          try {
            await Share.share({
              title: options.title,
              text: options.text,
              files: filesOutput,
            });
          } catch (error) {
            console.error(error);
            shareSucceeded = false;
          }
        }
      } else if (options.url) {
        try {
          await Share.share({
            title: options.title,
            text: options.text,
            url: options.url,
          });
        } catch (error) {
          console.error(error);
          shareSucceeded = false;
        }
      }
    } else {
      throw Error('Sharing is not allowed.');
    }

    return shareSucceeded;
  }

  public async shareUrl() {
    const url = this.getCurrentUrl();
    window.logger.info(`[native-share][share-url] sharing ${url}`);
    if (this.platform.is('hybrid')) {
      await this.share({
        url,
      });
    } else if ((await this.browserShareTarget) === 'teams') {
      this.popupCenter({
        url: `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&amp;preview=false`,
        title: 'Share in Teams',
        w: 600,
        h: 600,
      });
    }
  }

  private popupCenter({ url, title, w, h }) {
    // Fixes dual-screen position                             Most browsers      Firefox
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;
    const newWindow = window.open(
      url,
      title,
      `scrollbars=yes, width=${w / systemZoom}, height=${h / systemZoom}, top=${top}, left=${left}`
    );

    if (window.focus) {
      newWindow.focus();
    }
  }

  private getCurrentUrl(): string {
    const path = this._location.path();
    if (this.platform.is('hybrid')) {
      const branch = this.environmentService.branch;
      return `https://${branch !== 'production' ? `${branch}.` : ''}app.halloalberta.de${path}`;
    }
    return `${window.location.origin}${path}`;
  }
}

export interface ShareOptions {
  /** will be displayed in email header */
  title?: string;
  /**  will be the message in mail/teams */
  text?: string;
  files?: {
    /**  needs to include file ending like .pdf*/
    filename?: string;
    base64?: string;
    uri?: string;
  }[];
  url?: string;
  /**  android only feature. will be shown in share dialogue. */
  dialogTitle?: string;
}
